<template>
  <box class="evolucao-chart-card mb-4" :title="t('Evolução Mensal')" data-testid="fechamentos-evolucao-chart">
    <template #actions>
      <select
        v-model="selectedMetric"
        class="form-select form-select-sm metric-select"
        data-testid="fechamentos-evolucao-chart-metric-select"
      >
        <option value="faturamento">{{ t('Faturamento') }}</option>
        <option value="lucro">{{ t('Lucro') }}</option>
        <option value="despesas">{{ t('Despesas') }}</option>
        <option value="clientes">{{ t('Clientes Atendidos') }}</option>
        <option value="ticket_medio">{{ t('Ticket Médio') }}</option>
      </select>
    </template>
    <div v-if="isLoading" class="text-center py-5">
      <spinner style="width: 40px; height: 40px;" />
      <p class="text-muted mt-3">{{ t('Carregando...') }}</p>
    </div>
    <div v-else-if="!chartData || chartData.length === 0" class="text-center py-5 text-muted">
      <i class="pi pi-chart-line" style="font-size: 3rem; opacity: 0.3;"></i>
      <p class="mt-3">{{ t('Sem dados para exibir') }}</p>
      <small>{{ t('Necessário pelo menos 2 fechamentos') }}</small>
    </div>
    <div v-else>
      <div ref="chartContainer" class="apex-chart-container">
        <div ref="chartRef" class="apex-chart"></div>
      </div>

      <!-- Summary Stats -->
      <div v-if="chartData && chartData.length > 0" class="row mt-4 pt-3 border-top">
        <div class="col-md-4 col-sm-12 mb-3">
          <div class="stat-item d-flex justify-content-between">
            <span class="stat-label text-muted">{{ t('Média do Período') }}:</span>
            <span class="stat-value fw-bold">{{ formatStatValue(mediaPeriodo) }}</span>
          </div>
        </div>
        <div class="col-md-4 col-sm-12 mb-3">
          <div class="stat-item d-flex justify-content-between">
            <span class="stat-label text-muted">{{ t('Maior Valor') }}:</span>
            <span class="stat-value fw-bold text-success">{{ formatStatValue(maiorValor) }}</span>
          </div>
        </div>
        <div class="col-md-4 col-sm-12 mb-3">
          <div class="stat-item d-flex justify-content-between">
            <span class="stat-label text-muted">{{ t('Menor Valor') }}:</span>
            <span class="stat-value fw-bold text-danger">{{ formatStatValue(menorValor) }}</span>
          </div>
        </div>
      </div>
    </div>
  </box>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';

interface FechamentoHistorico {
  id?: number | string;
  data_inicial?: string;
  data_final?: string;
  meta_faturamento?: number | null;
  meta_clientes?: number | null;
  faturamento_vendas_realizadas?: number | null;
  lucro_mensal?: number | null;
  total_despesas_pagas?: number | null;
  numero_clientes?: number | null;
  [key: string]: unknown;
}

type MetricKey = 'faturamento' | 'lucro' | 'despesas' | 'clientes' | 'ticket_medio';

interface ChartDataPoint {
  fechamento_id?: number | string;
  periodo: string;
  data_inicial?: string;
  data_final?: string;
  value: number;
  meta?: number;
  realizado?: number;
}

interface ApexChartsInstance {
  render: () => Promise<void> | void;
  destroy: () => void;
}

interface ApexChartsModule {
  default: new (el: HTMLElement, options: unknown) => ApexChartsInstance;
}

const { t, n } = useI18n();

const props = withDefaults(
  defineProps<{
    fechamentos?: FechamentoHistorico[];
  }>(),
  {
    fechamentos: () => [],
  }
);

const emit = defineEmits<{
  (e: 'metric-changed', metric: MetricKey): void;
}>();

const chartRef = ref<HTMLElement | null>(null);
const chartContainer = ref<HTMLElement | null>(null);
const isLoading = ref<boolean>(false);
const selectedMetric = ref<MetricKey>('faturamento');
const chartData = ref<ChartDataPoint[]>([]);
let chartInstance: ApexChartsInstance | null = null;

// Format values for display
const formatStatValue = (value: number): string => {
  if (selectedMetric.value === 'clientes') {
    return value?.toLocaleString('pt-BR') || '0';
  }
  return n(value || 0, 'currency');
};

// Prepare chart data based on selected metric
const prepareChartData = (): void => {
  if (!props.fechamentos || props.fechamentos.length === 0) {
    chartData.value = [];
    return;
  }

  const sortedFechamentos = [...props.fechamentos].sort((a, b) => {
    return new Date(a.data_inicial || 0).getTime() - new Date(b.data_inicial || 0).getTime();
  });

  chartData.value = sortedFechamentos.map(fechamento => {
    const baseData = {
      fechamento_id: fechamento.id,
      periodo: formatDatePeriodo(fechamento),
      data_inicial: fechamento.data_inicial,
      data_final: fechamento.data_final
    };

    switch (selectedMetric.value) {
      case 'faturamento':
        return {
          ...baseData,
          value: fechamento.meta_faturamento || fechamento.faturamento_vendas_realizadas || 0,
          meta: fechamento.meta_faturamento || 0,
          realizado: fechamento.faturamento_vendas_realizadas || 0
        };
      case 'lucro':
        return {
          ...baseData,
          value: fechamento.lucro_mensal || 0
        };
      case 'despesas':
        return {
          ...baseData,
          value: fechamento.total_despesas_pagas || 0
        };
      case 'clientes':
        return {
          ...baseData,
          value: fechamento.numero_clientes || 0,
          meta: fechamento.meta_clientes || 0
        };
      case 'ticket_medio': {
        const clientes = fechamento.numero_clientes || 0;
        const faturamento = fechamento.faturamento_vendas_realizadas || 0;
        return {
          ...baseData,
          value: clientes > 0 ? faturamento / clientes : 0
        };
      }
      default:
        return { ...baseData, value: 0 };
    }
  });

  log('FechamentoEvolucaoChart', 'prepareChartData', 'Chart data prepared', {
    metric: selectedMetric.value,
    dataPoints: chartData.value.length
  });
};

const formatDatePeriodo = (fechamento: FechamentoHistorico): string => {
  const dataInicio = new Date(fechamento.data_inicial || '');
  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${meses[dataInicio.getMonth()]}/${dataInicio.getFullYear()}`;
};

const mediaPeriodo = computed<number>(() => {
  if (chartData.value.length === 0) return 0;
  const sum = chartData.value.reduce((acc, item) => acc + (item.value || 0), 0);
  return sum / chartData.value.length;
});

const maiorValor = computed<number>(() => {
  if (chartData.value.length === 0) return 0;
  return Math.max(...chartData.value.map(item => item.value || 0));
});

const menorValor = computed<number>(() => {
  if (chartData.value.length === 0) return 0;
  return Math.min(...chartData.value.map(item => item.value || 0));
});

const renderChart = (): void => {
  if (!chartRef.value || chartData.value.length === 0) return;

  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }

  const categories = chartData.value.map(d => d.periodo);
  const seriesData = chartData.value.map(d => d.value);

  const series: { name: string; data: number[] }[] = [{
    name: getSeriesName(),
    data: seriesData
  }];

  if (chartData.value.some(d => d.meta !== undefined) && ['faturamento', 'clientes'].includes(selectedMetric.value)) {
    series.push({
      name: t('Meta'),
      data: chartData.value.map(d => d.meta || 0)
    });
  }

  const options = {
    series: series,
    chart: {
      type: 'area',
      height: 280,
      toolbar: { show: false },
      fontFamily: 'inherit',
      background: 'transparent'
    },
    plotOptions: {
      area: {
        fillTo: 'end',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.05,
          stops: [0, 90, 100]
        }
      }
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: 'smooth',
      width: 2
    },
    xaxis: {
      categories: categories,
      labels: {
        style: { fontSize: '12px' }
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        formatter: (value: number): string => selectedMetric.value === 'clientes' ? (value?.toLocaleString('pt-BR') || '0') : n(value),
        style: { fontSize: '12px' }
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.05,
        stops: [0, 90, 100]
      }
    },
    colors: ['#3b82f6', '#9ca3af'],
    tooltip: {
      y: {
        formatter: (value: number, ctx: { seriesIndex: number; dataPointIndex: number }): string => {
          const label = series[ctx.seriesIndex].name;
          const formatted = selectedMetric.value === 'clientes'
            ? (value?.toLocaleString('pt-BR') || '0')
            : n(value);
          return `<strong>${label}:</strong> ${formatted}`;
        }
      }
    },
    legend: {
      position: 'top',
      fontSize: '14px'
    },
    grid: {
      borderColor: '#e5e7eb',
      strokeDashArray: 4,
      yaxis: { lines: { show: true } }
    },
    markers: { size: 4, hover: { size: 6 } },
    responsive: [{
      breakpoint: 768,
      options: {
        chart: { height: 220 },
        xaxis: {
          labels: {
            style: { fontSize: '10px' },
            rotate: -45
          }
        }
      }
    }]
  };

  import('apexcharts').then((mod: ApexChartsModule) => {
    if (chartRef.value) {
      chartInstance = new mod.default(chartRef.value, options);
      chartInstance.render();
      log('FechamentoEvolucaoChart', 'renderChart', 'Chart rendered');
    }
  }).catch((err: unknown) => console.error('Failed to load ApexCharts:', err));
};

const getSeriesName = (): string => {
  const names: Record<MetricKey, string> = {
    faturamento: t('Faturamento'),
    lucro: t('Lucro'),
    despesas: t('Despesas'),
    clientes: t('Clientes Atendidos'),
    ticket_medio: t('Ticket Médio')
  };
  return names[selectedMetric.value] || t('Valor');
};

const refreshChart = (): void => {
  prepareChartData();
  nextTick(() => renderChart());
};

watch(() => props.fechamentos, () => refreshChart(), { deep: true });
watch(selectedMetric, () => {
  emit('metric-changed', selectedMetric.value);
  refreshChart();
});

onMounted(() => refreshChart());

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }
});
</script>

<style scoped>
.evolucao-chart-card {
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.metric-select {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.15s;
}

.metric-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.apex-chart-container {
  min-height: 300px;
}

.apex-chart {
  width: 100%;
}

.stat-item {
  padding: 0.5rem 0;
}

.stat-label {
  font-size: 0.875rem;
  font-weight: 500;
}

.stat-value {
  font-size: 1rem;
  color: #111827;
}

@media (max-width: 768px) {
  .apex-chart-container {
    min-height: 250px;
  }

  .metric-select {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
}
</style>
